import { Box, Flex, Text } from '@chakra-ui/react';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonIcon,
  Checkbox,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table
} from '@morressier/alembic';
import {
  IReviewer,
  IReviewerInvitationQueue,
  IUser
} from '@publishing-engine/domain/models';
import { IReviewerInvitationResponse } from 'src/api/queries';
import { useSubmissionData } from '../../../../contexts/SubmissionDataProvider';
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import {
  restrictToParentElement,
  restrictToVerticalAxis
} from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { useState, useEffect } from 'react';
import ProtectedWorkflowAction from '../../../ProtectedAction/ProtectedWorkflowAction';
import UserName from 'src/components/User/UserName';

interface SortableRowProps {
  id: string;
  canManageQueue: boolean;
  children: React.ReactNode;
  selectedEmails: string[];
  setSelectedEmails: React.Dispatch<React.SetStateAction<string[]>>;
  isUserDeactivated: boolean;
}

const SortableRow = ({
  id,
  canManageQueue,
  children,
  selectedEmails,
  setSelectedEmails,
  isUserDeactivated
}: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? 'white' : undefined
  };

  const handleSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedEmails([...selectedEmails, e.target.value]);
    } else {
      const filtered = [...selectedEmails].filter(
        email => email !== e.target.value
      );
      setSelectedEmails(filtered);
    }
  };

  return (
    <Flex
      ref={setNodeRef}
      style={style}
      alignItems="center"
      padding={2}
      borderBottom={1}
      borderColor="neutralPrimaryBorder"
      borderBottomWidth="1px"
      borderStyle="solid"
    >
      <Table.RowCell>
        <Checkbox
          ariaLabel="Select Reviewer"
          id="select-reviewer"
          name="selectReviewer"
          value={id}
          onChange={e => handleSelected(e)}
          isChecked={selectedEmails.includes(id)}
          isDisabled={isUserDeactivated}
        >
          {' '}
        </Checkbox>
      </Table.RowCell>
      {canManageQueue && (
        <Table.RowCell>
          <ButtonIcon
            {...attributes}
            {...listeners}
            icon={<FontAwesomeIcon icon={faGripDotsVertical} />}
            color="transparent"
            ariaLabel="Drag reviewer"
            cursor="grab"
          />
        </Table.RowCell>
      )}
      {children}
    </Flex>
  );
};

interface Props {
  reviewerInvitations?: IReviewerInvitationResponse;
  onViewReviewerDetails: (reviewer: IReviewer) => void;
  onRemoveEnqueuedReviewer: (reviewerEmail: string[]) => void;
  onInviteNowReviewer: (reviewerEmail: string[]) => void;
  onInviteAndConfigReviewer: (reviewerEmail: string[]) => void;
  reorderReviewerQueue: (
    newPosition: number,
    currentPosition: number
  ) => Promise<boolean>;
  isProcessing: boolean;
}

const renderMenuItem = (
  label: string,
  onClick: () => void,
  textColor?: string,
  isDisabled?: boolean
) => {
  return (
    <Box onClick={event => event.stopPropagation()}>
      <MenuItem ariaLabel={label} onClick={onClick} isDisabled={isDisabled} disabled={isDisabled}>
        <Text color={textColor}>{label}</Text>
      </MenuItem>
    </Box>
  );
};

const ReviewQueueTable = ({
  reviewerInvitations,
  onViewReviewerDetails,
  onRemoveEnqueuedReviewer,
  onInviteNowReviewer,
  onInviteAndConfigReviewer,
  reorderReviewerQueue,
  isProcessing
}: Props) => {
  const { availableActions } = useSubmissionData();
  const canManageQueue = availableActions.contains('EnqueueReviewRequest');

  const queue = reviewerInvitations?.queue || [];
  const userDetails = reviewerInvitations?.userDetails || [];

  const [localQueue, setLocalQueue] = useState(queue);
  const [selectedReviewers, setSelectedReviewers] = useState<string[]>([]);

  useEffect(() => {
    setLocalQueue(reviewerInvitations?.queue ?? []);
  }, [reviewerInvitations?.queue]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const getReviewerDetails = (email: string) => {
    const reviewer = userDetails.find(user => user.email === email);
    return reviewer;
  };

  const renderMenu = (
    invitation: IReviewerInvitationQueue,
    reviewer?: IUser
  ) => {
    return (
      <div style={{ position: 'relative' }}>
        <Menu ariaLabel="Queue Actions">
          <MenuButton
            as={ButtonIcon}
            onClick={event => {
              event.stopPropagation();
            }}
            icon={<FontAwesomeIcon icon={faEllipsis} />}
            color="transparent"
          />

          <MenuList
            style={{ position: 'absolute', zIndex: 1000, bottom: '4px' }}
          >
            {reviewer &&
              renderMenuItem(
                'View Reviewer Profile',
                () => onViewReviewerDetails(reviewer),
                undefined,
                reviewer?.isUserDeactivated ?? false
              )}

            {renderMenuItem(
              'Invite Now',
              () => onInviteNowReviewer([invitation.email]),
              '',
              reviewer?.isUserDeactivated || isProcessing
            )}

            {renderMenuItem(
              'Edit & Invite',
              () => onInviteAndConfigReviewer([invitation.email]),
              '',
              reviewer?.isUserDeactivated || isProcessing
            )}
            {renderMenuItem(
              'Remove from Queue',
              () => onRemoveEnqueuedReviewer([invitation?.email!]),
              'actionSecondaryCautionContent'
            )}
          </MenuList>
        </Menu>
      </div>
    );
  };

  const renderRow = (invitation: IReviewerInvitationQueue) => {
    const reviewer = getReviewerDetails(invitation.email);
    return (
      <SortableRow
        key={invitation.email}
        id={invitation.email}
        canManageQueue={canManageQueue}
        selectedEmails={selectedReviewers}
        setSelectedEmails={setSelectedReviewers}
        isUserDeactivated={reviewer?.isUserDeactivated ?? false}
      >
        <Table.RowCell
          flex={1}
          _hover={{
            bg: reviewer ? 'gray.50' : undefined
          }}
          onClick={() => reviewer && onViewReviewerDetails(reviewer)}
        >
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Flex flexDirection="row" gap={2} alignItems="center">
              <Flex flexDirection="column">
                {reviewer ? (
                  <>
                    <UserName
                      firstName={
                        reviewer.firstName === 'Invited' &&
                        reviewer.lastName === 'User'
                          ? invitation.email
                          : `${reviewer.firstName} ${reviewer.lastName}`
                      }
                      isUserDeactivated={reviewer?.isUserDeactivated ?? false}
                      tooltipLabel={'View Reviewer Profile'}
                    />
                    <Text textStyle="headingOverline">
                      {reviewer.affiliationDetails?.organizationName}
                    </Text>
                  </>
                ) : (
                  <Text textStyle="headingLine">{invitation.email}</Text>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Table.RowCell>
        <Table.RowCell>
          {' '}
          {canManageQueue && renderMenu(invitation, reviewer)}
        </Table.RowCell>
      </SortableRow>
    );
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localQueue.findIndex(item => item.email === active.id);
    const newIndex = localQueue.findIndex(item => item.email === over.id);

    const previousQueue = localQueue;
    setLocalQueue(arrayMove(localQueue, oldIndex, newIndex));

    const success = await reorderReviewerQueue(newIndex, oldIndex);
    if (!success) {
      setLocalQueue(previousQueue);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedReviewers(
        localQueue
          .filter(item => {
            const reviewer = getReviewerDetails(item.email);
            return !reviewer?.isUserDeactivated;
          })
          .map(item => item.email)
      );
    } else {
      setSelectedReviewers([]);
    }
  };

  const isAllSelected =
    localQueue.filter(item => {
      const reviewer = getReviewerDetails(item.email);
      return !reviewer?.isUserDeactivated;
    }).length === selectedReviewers.length;

  return (
    <Flex gap={2} flexDirection="column">
      <Flex justifyContent="space-between" alignItems="center" paddingLeft={2}>
        <ProtectedWorkflowAction actionName="EnqueueReviewRequest">
          <Checkbox
            id="select-all"
            name="selectAll"
            ariaLabel="Select All"
            onChange={e => handleSelectAll(e)}
            isChecked={isAllSelected}
            disabled={!canManageQueue}
          >
            Select All
          </Checkbox>
        </ProtectedWorkflowAction>
        <ProtectedWorkflowAction actionName="EnqueueReviewRequest">
          <Menu ariaLabel="All Queue Actions">
            <MenuButton
              as={Button}
              iconRight={<FontAwesomeIcon icon={faEllipsis} />}
              variant="secondary"
              disabled={!selectedReviewers.length || !canManageQueue}
            >
              Actions
            </MenuButton>
            <MenuList>
              <MenuItem
                ariaLabel="Invite"
                onClick={() => onInviteNowReviewer(selectedReviewers)}
              >
                Invite Now
              </MenuItem>
              <MenuItem
                ariaLabel="Edit & Invite"
                onClick={() => onInviteAndConfigReviewer(selectedReviewers)}
              >
                Edit & Invite
              </MenuItem>
              <MenuItem
                ariaLabel="Remove All"
                onClick={() => onRemoveEnqueuedReviewer(selectedReviewers)}
              >
                <Text color="actionSecondaryCautionContent">
                  Remove from Queue
                </Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </ProtectedWorkflowAction>
      </Flex>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <SortableContext
          items={localQueue.map(item => item.email)}
          strategy={verticalListSortingStrategy}
        >
          <Table>
            <Table.Body>{localQueue.map(renderRow)}</Table.Body>
          </Table>
        </SortableContext>
      </DndContext>
    </Flex>
  );
};

export default ReviewQueueTable;
